import React, { useState,useEffect } from 'react';
import { Table, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    capitalizeFirstLetter,
    formatDateDMY,
    generateUniqueTimestamp,
    getDataUsingUserId,
    getDataUuid,
} from '@utils/sharedUtils';
import {  useAppSelector } from '@store/hooks';
import Swal from 'sweetalert2';
import { TbDeviceIpadX } from 'react-icons/tb';
const MySwal = Swal;
type DeviceDetails = {
    name: string;
    device_id: string;
    date: string;
    id: string;
};
const NurseAssignedDeviceList: React.FC= () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [deviceDetails, setDeviceDetails] = useState<DeviceDetails[]>([]);
    const loggedInUserCognitoUuid = useAppSelector((state) => state.login.loggedInUserCognitoUuid);
    useEffect(() => {
        fetchDeviceDetails();
    }, []);
    const fetchDeviceDetails = async () => {
        try {
                const nurse = await getDataUsingUserId(
                    'nurse_device',
                    loggedInUserCognitoUuid
                );
                let assigned = [];
                if (nurse && nurse.Items && nurse.Items.length > 0) {
                    for (const device of nurse.Items) {
                        const deviceData = await getDataUuid(
                            'devices',
                            device?.device_id?.S ? device?.device_id?.S : ''
                        );
                        if (deviceData) {
                            const assignedDevice = deviceData;
                            const data = {
                                name: assignedDevice?.device_name?.S
                                    ? capitalizeFirstLetter(
                                          assignedDevice?.device_name?.S
                                      )
                                    : '',
                                device_id: assignedDevice?.device_id?.S
                                    ? capitalizeFirstLetter(
                                          assignedDevice?.device_id?.S
                                      )
                                    : '',
                                date: assignedDevice?.created_at?.S
                                    ? formatDateDMY(
                                          assignedDevice?.created_at?.S
                                      )
                                    : '',
                                id: assignedDevice?.uuid?.S
                                    ? assignedDevice?.uuid?.S
                                    : '',
                            };
                            assigned.push(data);
                        }
                    }
                    setDeviceDetails(assigned);
                } 
        } catch (error: any) {
            let errorMessage;
            switch (error.name) {
                case 'ResourceNotFoundException':
                    errorMessage = 'Error: The specified table does not exist.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage =
                        'Error: The request rate is too high. Please try again later.';
                    break;
                case 'ValidationException':
                    errorMessage =
                        'Error: The provided key element does not match the schema.';
                    break;
                case 'DynamoDBServiceException':
                    errorMessage = `Service Error: ${error.message}`;
                    break;
                default:
                    (errorMessage = 'An unknown error occurred:'), error;
                    break;
            }
            MySwal.fire({
                position: 'top-end', // Position the toast in the top-right corner
                icon: 'error',
                title: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                customClass: {
                    popup: '!text-red !bg-light_red', // Add custom class to the popup
                },
                width: 'auto',
            });
            // setError(error.message);
            // console.log('error:', error.message);
        } finally {
            setLoading(false);
        }
    };
    const totalPages = Math.ceil(deviceDetails.length / rowsPerPage);

    const handlePageChange = (page: number) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const currentData = deviceDetails.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const disablePrevious = currentPage === 1;
    const disableNext = currentPage === totalPages;

    const { t } = useTranslation();

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        handlePageChange(1); // Reset to first page
    };

    const goToPreviousPage = () => {
        if (!disablePrevious) {
            handlePageChange(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (!disableNext) {
            handlePageChange(currentPage + 1);
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 3;

        if (totalPages <= 6) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(renderPageButton(i));
            }
        } else {
            if (currentPage <= maxVisiblePages) {
                for (let i = 1; i <= maxVisiblePages + 1; i++) {
                    pageNumbers.push(renderPageButton(i));
                }
                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            } else if (currentPage >= totalPages - maxVisiblePages + 1) {
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());
                for (
                    let i = totalPages - maxVisiblePages + 1;
                    i <= totalPages;
                    i++
                ) {
                    pageNumbers.push(renderPageButton(i));
                }
            } else {
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());

                const startPage = currentPage - 1;
                const endPage = currentPage + 1;

                for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(renderPageButton(i));
                }

                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            }
        }

        return pageNumbers;
    };

    const renderPageButton = (pageNumber: number) => (
        <button
            key={pageNumber}
            className={`mx-1 pagination-padding py-2 border rounded ${pageNumber === currentPage ? '!bg-primary text-white' : 'bg-white text-black'}`}
            onClick={() => handlePageChange(pageNumber)}
        >
            {pageNumber}
        </button>
    );

    const renderEllipsis = () => (
        <span key="ellipsis" className="mx-1 py-2">
            ...
        </span>
    );

    return (
        <div
            className="bg-white flex flex-col rounded-2xl"
            // style={{ width: '25rem' }}
        >
            <Table className='text-center'>
                <thead>
                    <tr
                        className="border"
                    >

                        <th className="border-none font-semibold">{t('nurse_details.device_name')}</th>
                        <th className="border-none font-semibold">{t('nurse_details.device_id')}</th>
                        <th className="border-none font-semibold">{t('nurse_details.assigned_date')}</th>
                    </tr>
                </thead>
                <tbody>
                {deviceDetails.length > 0 ? ( 
                    <>
                        {currentData.map((userDevice, index) => (
                            <tr className="border-none" key={index}>
                                <td className="border-none text-black">
                                    {userDevice.name}
                                </td>
                                <td className="border-none text-black">
                                    {userDevice.device_id}
                                </td>
                                <td className="border-none text-black">
                                    {userDevice.date}
                                </td>
                            </tr>
                        ))}
                    </>
                ) : (
                    <>
                        <tr className="border-none">
                            <td
                                className="border-none text-center py-5"
                                colSpan={3}
                            >
                                <div className="flex justify-center">
                                    <TbDeviceIpadX
                                        size={40}
                                        className="text-primary"
                                    />
                                </div>
                                {t('nurse_details.no_data')}
                            </td>
                        </tr>
                   </>
                )}

                </tbody>
            </Table>
            <div className="pb-4 mt-5 flex justify-between items-center absolute -bottom-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="flex justify-center mx-auto">
                    <button
                        className="btn bg-white text-black text-sm p-1"
                        onClick={goToPreviousPage}
                        disabled={
                            deviceDetails.length == 0 ||
                            currentPage === 1 ||
                            disablePrevious
                        }
                    >
                        {t('user_management.pagination.previous')}
                    </button>
                    {renderPageNumbers()}
                    <button
                        className="btn bg-white text-black text-sm p-1"
                        onClick={goToNextPage}
                        disabled={
                            deviceDetails.length == 0 ||
                            currentPage === totalPages ||
                            disableNext
                        }
                    >
                        {t('user_management.pagination.next')}
                    </button>
                </div>
            </div>
        </div>
    );
};
export default NurseAssignedDeviceList;
